import React, { useEffect, useState } from "react";
import { useI18n } from "../../i18n/I18nContext";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Receipt } from "lucide-react";

export default function Cashier() {
  const { t, localize, fmtSAR, isAr } = useI18n();
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [ticket, setTicket] = useState([]);
  const [customer, setCustomer] = useState("");
  const [shift, setShift] = useState(null);
  const [branchId, setBranchId] = useState("");
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/branches").then((r) => { setBranches(r.data); setBranchId(r.data[0]?.id || ""); }).catch(() => {});
  }, []);

  const addItem = (it) => setTicket((prev) => {
    const ex = prev.find((x) => x.id === it.id);
    if (ex) return prev.map((x) => x.id === it.id ? { ...x, qty: x.qty + 1 } : x);
    return [...prev, { id: it.id, name_ar: it.name_ar, name_en: it.name_en, price: it.price, qty: 1 }];
  });
  const removeItem = (id) => setTicket((prev) => prev.filter((x) => x.id !== id));

  const subtotal = ticket.reduce((s, i) => s + i.price * i.qty, 0);
  const vat = subtotal * 0.15;
  const total = subtotal + vat;

  const openShift = async () => {
    try { const { data } = await api.post("/admin/cashier/shift/open", { branch_id: branchId, opening_float: 0 }); setShift(data); toast.success(t("admin.open_shift")); }
    catch (e) { toast.error(apiError(e)); }
  };
  const closeShift = async () => {
    try { const { data } = await api.post(`/admin/cashier/shift/${shift.id}/close`); toast.success(`${t("admin.z_report")}: ${fmtSAR(data.total)}`); setShift(null); }
    catch (e) { toast.error(apiError(e)); }
  };
  const checkout = async () => {
    if (ticket.length === 0) return;
    try {
      const { data } = await api.post("/admin/cashier/invoice", { customer_name: customer || "Walk-in", items: ticket.map((i) => ({ name_ar: i.name_ar, name_en: i.name_en, price: i.price, qty: i.qty })), shift_id: shift?.id });
      setInvoice(data); setTicket([]); toast.success(isAr ? "تم إنشاء الفاتورة" : "Invoice created");
    } catch (e) { toast.error(apiError(e)); }
  };

  const printReceipt = (inv) => {
    const rows = inv.items.map((i) => `<tr><td>${isAr ? i.name_ar : i.name_en} ×${i.qty}</td><td style="text-align:${isAr ? "left" : "right"}">${(i.price * i.qty).toFixed(2)}</td></tr>`).join("");
    const w = window.open("", "_blank", "width=320,height=600");
    w.document.write(`<!doctype html><html dir="${isAr ? "rtl" : "ltr"}"><head><meta charset="utf-8"><title>${inv.number}</title>
    <style>@page{size:80mm auto;margin:0}body{width:80mm;margin:0;padding:8px;font-family:${isAr ? "'Tahoma',sans-serif" : "monospace"};font-size:12px;color:#000}
    img{display:block;margin:0 auto 6px;height:38px}h4{text-align:center;margin:2px 0}table{width:100%;border-collapse:collapse}td{padding:2px 0}
    .line{border-top:1px dashed #000;margin:6px 0}.tot{font-weight:bold;font-size:14px}.c{text-align:center}.muted{color:#444;font-size:10px}</style></head>
    <body onload="window.print();setTimeout(()=>window.close(),300)">
    <img src="/logo-motawahj.png"/><h4>${isAr ? "متوهج للتصوير" : "Motawahj Studio"}</h4>
    <div class="c muted">${inv.number} · ${new Date(inv.at).toLocaleString("en-GB", { timeZone: "Asia/Riyadh" })}</div>
    <div class="c muted">${isAr ? "العميل" : "Customer"}: ${inv.customer_name}</div>
    <div class="line"></div><table>${rows}</table><div class="line"></div>
    <table><tr><td>${isAr ? "المجموع" : "Subtotal"}</td><td style="text-align:${isAr ? "left" : "right"}">${inv.subtotal.toFixed(2)}</td></tr>
    <tr><td>${isAr ? "ضريبة القيمة المضافة" : "VAT"} (${Math.round(inv.vat_rate * 100)}%)</td><td style="text-align:${isAr ? "left" : "right"}">${inv.vat.toFixed(2)}</td></tr>
    <tr class="tot"><td>${isAr ? "الإجمالي شامل الضريبة" : "Total incl. VAT"}</td><td style="text-align:${isAr ? "left" : "right"}">${inv.total.toFixed(2)} ${isAr ? "ر.س" : "SAR"}</td></tr></table>
    <div class="line"></div><div class="c muted">ZATCA: ${inv.zatca_status} — ${isAr ? "نسخة عرض" : "display copy"}</div>
    <div class="c muted">${isAr ? "شكراً لزيارتكم" : "Thank you"}</div></body></html>`);
    w.document.close();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-slate-900">{t("admin.pos")}</h1>
        <div className="flex items-center gap-2">
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="rounded-lg border border-border px-3 py-1.5 text-sm">{branches.map((b) => <option key={b.id} value={b.id}>{isAr ? b.name_ar : b.name_en}</option>)}</select>
          {shift ? <button onClick={closeShift} data-testid="close-shift-btn" className="rounded-full bg-[hsl(var(--brand-red))] px-4 py-1.5 text-sm font-medium text-white">{t("admin.close_shift")}</button>
                 : <button onClick={openShift} data-testid="open-shift-btn" className="rounded-full bg-green-600 px-4 py-1.5 text-sm font-medium text-white">{t("admin.open_shift")}</button>}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h3 className="mb-2 text-sm font-semibold text-slate-500">{t("admin.services")}</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {services.map((s) => (
              <button key={s.id} onClick={() => addItem(s)} data-testid={`pos-service-${s.id}`} className="rounded-xl border border-border bg-white p-3 text-start transition-all hover:border-primary hover:shadow">
                <div className="text-sm font-medium text-slate-900">{localize(s, "name")}</div><div className="text-xs text-primary">{fmtSAR(s.price)}</div>
              </button>
            ))}
          </div>
          <h3 className="mb-2 mt-5 text-sm font-semibold text-slate-500">{t("nav.shop")}</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {products.map((p) => (
              <button key={p.id} onClick={() => addItem(p)} data-testid={`pos-product-${p.id}`} className="rounded-xl border border-border bg-white p-3 text-start transition-all hover:border-primary hover:shadow">
                <div className="text-sm font-medium text-slate-900">{localize(p, "name")}</div><div className="text-xs text-primary">{fmtSAR(p.price)}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-white p-4">
          <input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder={t("admin.customers")} data-testid="pos-customer" className="mb-3 w-full rounded-lg border border-border px-3 py-2 text-sm" />
          <div className="min-h-[120px] space-y-2">
            {ticket.length === 0 && <p className="py-8 text-center text-sm text-slate-300">{t("common.none")}</p>}
            {ticket.map((i) => (
              <div key={i.id} className="flex items-center justify-between text-sm">
                <span className="flex-1">{localize(i, "name")} <span className="text-slate-400">×{i.qty}</span></span>
                <span className="font-medium">{fmtSAR(i.price * i.qty)}</span>
                <button onClick={() => removeItem(i.id)} className="ms-2 text-[hsl(var(--brand-red))]"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-1 border-t border-border pt-3 text-sm">
            <div className="flex justify-between"><span>{t("common.subtotal")}</span><span>{fmtSAR(subtotal)}</span></div>
            <div className="flex justify-between text-slate-500"><span>{t("common.vat")} 15%</span><span>{fmtSAR(vat)}</span></div>
            <div className="flex justify-between text-base font-bold"><span>{t("common.total")}</span><span className="text-primary">{fmtSAR(total)}</span></div>
          </div>
          <button onClick={checkout} disabled={ticket.length === 0} data-testid="pos-checkout-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-white disabled:opacity-40"><Receipt className="h-4 w-4" /> {isAr ? "إصدار فاتورة" : "Create Invoice"}</button>
          <p className="mt-2 text-center text-xs text-amber-600">{isAr ? "فاتورة عرض PDF — غير متوافقة مع ZATCA بعد" : "Display PDF — not ZATCA-compliant yet"}</p>
        </div>
      </div>

      {invoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setInvoice(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-center"><img src="/logo-motawahj.png" alt="" className="mx-auto h-10" /><div className="mt-2 font-mono text-sm">{invoice.number}</div></div>
            <div className="mt-4 space-y-1 text-sm">
              {invoice.items.map((i, x) => <div key={x} className="flex justify-between"><span>{localize(i, "name")} ×{i.qty}</span><span>{fmtSAR(i.price * i.qty)}</span></div>)}
              <div className="flex justify-between border-t border-border pt-2"><span>{t("common.vat")}</span><span>{fmtSAR(invoice.vat)}</span></div>
              <div className="flex justify-between font-bold"><span>{t("common.total")}</span><span>{fmtSAR(invoice.total)}</span></div>
            </div>
            <div className="mt-3 flex justify-between text-sm"><span>{t("common.subtotal")}</span><span>{fmtSAR(invoice.subtotal)}</span></div>
            <div className="mt-3 rounded bg-amber-50 p-2 text-center text-xs text-amber-700">ZATCA: {invoice.zatca_status} — {isAr ? "نسخة عرض PDF (غير نظامية بعد)" : "display PDF (not compliant yet)"}</div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => printReceipt(invoice)} data-testid="print-invoice-btn" className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[hsl(var(--brand-orange))] py-2.5 text-sm font-semibold text-white"><Receipt className="h-4 w-4" /> {isAr ? "طباعة" : "Print"}</button>
              <button onClick={() => setInvoice(null)} className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold text-slate-700">{t("common.close")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
